// OnboardingTour.tsx
// ---------------------------------------------------------------------------
// A non-locking, adaptive spotlight tour shown once after a player creates
// their first character (from the Roster, a guild invite, a pug invite —
// wherever). It walks them through the 4 things that actually matter, in
// priority order:
//
//   1. Paint availability  2. Confirm each reset  3. Plan next week  4. Join guild
//   5. Sync character  6. Set BiS list  7. Join squad
//
// Design principles
// -----------------
// * NEVER traps the user. The dim backdrop is painted with a huge `box-shadow`
//   on a `pointer-events:none` element, so every pixel of the page (even the
//   dimmed parts) stays clickable. Players can use the highlighted feature,
//   click elsewhere, or wander off at any time.
// * Adapts. Steps anchor to real DOM nodes via `[data-tour]`. If the player
//   leaves the page the tour gracefully collapses into a small "Continue setup"
//   pill instead of yanking them back; returning re-engages it. If a target
//   hasn't rendered yet (async fetch) it waits, then skips that step rather
//   than getting stuck.
// * Right place, right time. Character creation can happen mid-pug or mid-invite
//   on a different screen, so creation only *queues* the tour (a pending flag).
//   It engages the moment the player is looking at the Roster — the one page
//   where all 4 features live — and otherwise waits politely as the pill.
// * One time. A localStorage flag means a player sees it once, ever (matching
//   the existing TutorialHelper persistence pattern — no server/DB changes).
//
// Public API
// ----------
//   requestOnboardingTour()        -> call after a character is created anywhere
//   startOnboardingTour()          -> force-replay (e.g. a "Show me again" button)
//   hasCompletedOnboardingTour()   -> bool
//   resetOnboardingTour()          -> clear the flag (debugging)
// ---------------------------------------------------------------------------
import React, { useState, useEffect, useRef, useCallback, useLayoutEffect, useMemo } from "react";

export const ONBOARDING_TOUR_KEY = "lfm_onboarding_tour_v1";
const ONBOARDING_NAME_KEY = "lfm_onboarding_tour_char";
const TOUR_EVENT = "lfm:onboarding-tour";

function readName(): string {
  try {
    return localStorage.getItem(ONBOARDING_NAME_KEY) || "";
  } catch {
    return "";
  }
}

type FlagValue = "pending" | "done" | null;

function readFlag(): FlagValue {
  try {
    return (localStorage.getItem(ONBOARDING_TOUR_KEY) as FlagValue) ?? null;
  } catch {
    return null;
  }
}
function writeFlag(v: Exclude<FlagValue, null>) {
  try {
    localStorage.setItem(ONBOARDING_TOUR_KEY, v);
  } catch {
    /* ignore (private mode etc.) */
  }
}

/** Has the player already finished / dismissed the tour? */
export function hasCompletedOnboardingTour(): boolean {
  return readFlag() === "done";
}

/**
 * Queue the tour. Call this right after a character is created from ANY entry
 * point. No-op if the player has already seen it. Engages immediately if they
 * are on the Roster, otherwise waits as a gentle pill until they get there.
 */
export function requestOnboardingTour(characterName?: string) {
  if (readFlag() === "done") return;
  writeFlag("pending");
  if (characterName) {
    try {
      localStorage.setItem(ONBOARDING_NAME_KEY, characterName);
    } catch {
      /* ignore */
    }
  }
  window.dispatchEvent(new CustomEvent(TOUR_EVENT, { detail: { action: "request", characterName } }));
}

/** Force the tour to run from the start, ignoring the one-time flag. */
export function startOnboardingTour() {
  window.dispatchEvent(new CustomEvent(TOUR_EVENT, { detail: { action: "start" } }));
}

/** Clear the one-time flag (handy for QA / a settings "reset tutorial" button). */
export function resetOnboardingTour() {
  try {
    localStorage.removeItem(ONBOARDING_TOUR_KEY);
  } catch {
    /* ignore */
  }
}

// --- Steps ------------------------------------------------------------------

type Placement = "top" | "bottom";

interface Step {
  key: string;
  tab: string; // the tab/page where this feature lives
  selector: string; // querySelector for the spotlight target
  title: string;
  body: string;
  placement: Placement; // preferred tooltip side (auto-flips if no room)
  radius: number; // spotlight corner radius
  icon: React.ReactNode;
  // Some targets live in a panel that only reveals on hover/focus (e.g. the
  // character card's slide-in Sync column). When set, the tour forces that
  // panel open for the duration of the step, then restores it.
  revealParent?: boolean;
  // "painted": Next stays locked until the target reports data-tour-painted="true"
  // (the availability grid has at least one hour). The player can still Skip.
  gate?: "painted";
  // Offer the quick-fill buttons (TOUR_QUICK_FILLS) inside the tooltip.
  quickFill?: boolean;
  // Scroll so the target's top sits this many px from the top of the screen.
  pinTop?: number;
  // Use the wider tooltip.
  wide?: boolean;
}

/**
 * One-click starting points for the availability step, in the player's own
 * timezone. RosterPage applies them on the "lfm:tour-quickfill" event and adds
 * them to whatever is already painted. Slots are half-hours (38 = 19:00).
 */
export const TOUR_QUICK_FILLS: { key: string; label: string; hint: string; daysMon0: number[]; fromSlot: number; toSlot: number }[] = [
  { key: "weeknights", label: "Weeknight evenings", hint: "Mon to Thu, 19:00 to 23:00", daysMon0: [0, 1, 2, 3], fromSlot: 38, toSlot: 46 },
  { key: "weekends", label: "Weekend evenings", hint: "Fri to Sun, 19:00 to 00:00", daysMon0: [4, 5, 6], fromSlot: 38, toSlot: 48 },
  { key: "everyday", label: "Every evening", hint: "Every day, 19:00 to 23:00", daysMon0: [0, 1, 2, 3, 4, 5, 6], fromSlot: 38, toSlot: 46 },
];

const ICON = {
  clock: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
  ),
  guild: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 21v-6m0 0l2.77-.693a9 9 0 016.208.682l.108.054a9 9 0 006.086.71l3.114-.732a48.524 48.524 0 01-.005-10.499l-3.11.732a9 9 0 01-6.085-.711l-.108-.054a9 9 0 00-6.208-.682L3 4.5M3 15V4.5" />
  ),
  sync: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  ),
  list: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2M9 12h6M9 16h6" />
  ),
  squad: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4zm6 0a3 3 0 10-2.5-4.66M3 9.34A3 3 0 015.5 4.68" />
  ),
  check: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  ),
  calendar: (
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  ),
};

const STEPS: Step[] = [
  {
    key: "availability",
    tab: "Roster",
    selector: '[data-tour="avail-grid"]',
    title: "Paint your raid hours",
    body:
      "The most important thing you'll set. Click and drag across the grid to mark when you can raid. Raid leaders and auto-scheduling only put you in raids inside these hours.",
    placement: "bottom",
    radius: 14,
    icon: ICON.clock,
    gate: "painted",
    quickFill: true,
    pinTop: 96,
    wide: true,
  },
  {
    key: "avail-confirm",
    tab: "Roster",
    selector: '[data-tour="avail-confirm"]',
    title: "Confirm each reset",
    body:
      "Painting your hours confirms that week for you. When a new reset comes round, this switch is how you say you're in, or that you're sitting it out. The Discord bot will ask you too. Leave it unset and leaders can't count on you.",
    placement: "bottom",
    radius: 14,
    icon: ICON.check,
  },
  {
    key: "avail-weeks",
    tab: "Roster",
    selector: '[data-tour="avail-weeks"]',
    title: "Plan the week ahead",
    body:
      "Raid leaders build next week's roster early, so the grid opens on Next Week. Switch to This Week if you can still raid this reset. On Next Week, Copy current week carries your hours over in one click.",
    placement: "bottom",
    radius: 12,
    icon: ICON.calendar,
  },
  {
    key: "guild",
    tab: "Roster",
    selector: '[data-tour="guild"]',
    title: "Join your guild",
    body:
      "Got an invite code from a guild leader? Pop it in here with the character you want to bring. Joining a guild plugs you into shared raids, loot tracking and your guild's schedule.",
    placement: "bottom",
    radius: 14,
    icon: ICON.guild,
  },
  {
    key: "sync",
    tab: "Roster",
    selector: '[data-tour="sync"]',
    title: "Sync your character",
    body:
      "One click pulls your live gear, portrait and Warcraft Logs parse straight from the armory. Keep it fresh so leaders see the real you when they pick the roster.",
    placement: "bottom",
    radius: 12,
    icon: ICON.sync,
    revealParent: true,
  },
  {
    key: "bis",
    tab: "Roster",
    selector: '[data-tour="bis"]',
    title: "Build your BiS list",
    body:
      "Flag the loot you want from each raid. Loot council sees your priorities instantly, so nobody has to shout \"who needs this?\" in raid chat when it drops.",
    placement: "bottom",
    radius: 12,
    icon: ICON.list,
  },
  {
    key: "squad",
    tab: "Roster",
    selector: '[data-tour="squad"]',
    title: "Join a squad",
    body:
      "Squads keep you and your friends together. Create one and share the code, or join with a friend's code. The scheduler then does its best to keep squadmates in the same raids.",
    placement: "top",
    radius: 16,
    icon: ICON.squad,
  },
];

// --- Geometry helpers -------------------------------------------------------

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const SPOT_PAD = 8; // breathing room around the highlighted element
const TIP_W = 372; // tooltip width
const TIP_W_WIDE = 560; // steps with extra controls (quick fill), so they stay short
const GAP = 16; // gap between spotlight and tooltip

function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v));
}

function rectsEqual(a: Rect | null, b: Rect | null) {
  if (!a || !b) return a === b;
  return (
    Math.abs(a.top - b.top) < 0.5 &&
    Math.abs(a.left - b.left) < 0.5 &&
    Math.abs(a.width - b.width) < 0.5 &&
    Math.abs(a.height - b.height) < 0.5
  );
}

interface OnboardingTourProps {
  /** The currently active tab in the host app. */
  currentTab?: string;
  /** Ask the host app to switch tabs (used only on explicit start / pill resume). */
  onRequestTab?: (tab: string) => void;
  /** Whether the player already has availability set — lets step 1 adapt its copy. */
  availabilitySet?: boolean;
}

export default function OnboardingTour({
  currentTab,
  onRequestTab,
  availabilitySet = false,
}: OnboardingTourProps) {
  const [phase, setPhase] = useState<"idle" | "active" | "celebrating">("idle");
  const [minimized, setMinimized] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const [tipH, setTipH] = useState(190); // measured tooltip height (for placement)
  const [moveAnim, setMoveAnim] = useState(false); // animate spotlight only on step changes
  const [charName, setCharName] = useState<string>(() => readName()); // just-created character, for the finale

  const step = STEPS[stepIndex];
  const isLast = stepIndex === STEPS.length - 1;

  // Refs to read fresh values inside event listeners without re-binding them.
  const currentTabRef = useRef(currentTab);
  currentTabRef.current = currentTab;
  const scrolledForStep = useRef<number>(-1);
  const lastRectRef = useRef<Rect | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);

  // --- Lifecycle: begin / advance / finish ---------------------------------

  const begin = useCallback(
    (forced: boolean) => {
      setStepIndex(0);
      scrolledForStep.current = -1;
      lastRectRef.current = null;
      setRect(null);
      setPhase("active");
      const onTargetTab = currentTabRef.current === STEPS[0].tab;
      if (forced) {
        // Explicit replay: take them to the right place and engage.
        setMinimized(false);
        if (!onTargetTab) onRequestTab?.(STEPS[0].tab);
      } else if (onTargetTab) {
        // Created while on the Roster — engage straight away.
        setMinimized(false);
      } else {
        // Created mid-flow elsewhere — don't interrupt; wait as a pill.
        setMinimized(true);
      }
    },
    [onRequestTab]
  );

  // Quiet exit (skip / dismiss) — no fanfare.
  const finish = useCallback(() => {
    writeFlag("done");
    setPhase("idle");
    setRect(null);
  }, []);

  // Earned exit (finished the last step) — roll the celebration.
  const complete = useCallback(() => {
    writeFlag("done");
    lastRectRef.current = null;
    setRect(null);
    setMinimized(false);
    setPhase("celebrating");
  }, []);

  const goNext = useCallback(() => {
    if (stepIndex >= STEPS.length - 1) {
      complete();
      return;
    }
    scrolledForStep.current = -1;
    lastRectRef.current = null;
    setRect(null);
    setStepIndex(stepIndex + 1);
  }, [stepIndex, complete]);

  const goBack = useCallback(() => {
    if (stepIndex <= 0) return;
    scrolledForStep.current = -1;
    lastRectRef.current = null;
    setRect(null);
    setStepIndex(stepIndex - 1);
  }, [stepIndex]);

  // Jump straight to a given step (used by the debug `goto`).
  const goToStep = useCallback(
    (idx: number) => {
      const i = Math.max(0, Math.min(STEPS.length - 1, idx));
      scrolledForStep.current = -1;
      lastRectRef.current = null;
      setRect(null);
      setStepIndex(i);
      setMinimized(false);
      setPhase("active");
      if (currentTabRef.current !== STEPS[i].tab) onRequestTab?.(STEPS[i].tab);
    },
    [onRequestTab]
  );

  // Mark the component as mounted so the debug API can warn if it isn't.
  useEffect(() => {
    try {
      (window as any).__lfmTourMounted = true;
    } catch {
      /* ignore */
    }
    return () => {
      try {
        (window as any).__lfmTourMounted = false;
      } catch {
        /* ignore */
      }
    };
  }, []);

  // Animate the spotlight's glide only when the step changes (snap during scroll).
  useEffect(() => {
    setMoveAnim(true);
    const t = window.setTimeout(() => setMoveAnim(false), 380);
    return () => window.clearTimeout(t);
  }, [stepIndex]);

  // --- Triggers: custom events + a queued pending flag on mount ------------

  useEffect(() => {
    const onEvt = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      const action = detail.action;
      if (action === "start") begin(true);
      else if (action === "request" && readFlag() !== "done") {
        if (detail.characterName) setCharName(detail.characterName);
        begin(false);
      } else if (action === "goto") goToStep(detail.index ?? 0);
      else if (action === "celebrate") {
        lastRectRef.current = null;
        setRect(null);
        setMinimized(false);
        setPhase("celebrating");
      }
    };
    window.addEventListener(TOUR_EVENT, onEvt);
    return () => window.removeEventListener(TOUR_EVENT, onEvt);
  }, [begin, goToStep]);

  // If a tour was queued earlier (e.g. created on a pug page, then the app
  // reloaded) honour it on mount — but politely, as a pill, never yanking.
  useEffect(() => {
    if (readFlag() === "pending") {
      setStepIndex(0);
      setPhase("active");
      setMinimized(currentTabRef.current !== STEPS[0].tab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Tab watcher: collapse when they leave, re-engage when they return ---

  useEffect(() => {
    if (phase !== "active") return;
    if (currentTab === step.tab) {
      if (minimized) {
        scrolledForStep.current = -1;
        lastRectRef.current = null;
        setMinimized(false);
      }
    } else if (!minimized) {
      setMinimized(true);
      lastRectRef.current = null;
      setRect(null);
    }
  }, [currentTab, phase, step.tab, minimized]);

  // --- Target finding + live position tracking -----------------------------

  const engaged = phase === "active" && !minimized && currentTab === step.tab;

  useEffect(() => {
    if (!engaged) return;
    let cancelled = false;
    let findTimer = 0;
    let attempts = 0;

    const measure = (el: HTMLElement): Rect => {
      const r = el.getBoundingClientRect();
      return { top: r.top, left: r.left, width: r.width, height: r.height };
    };

    const commit = (next: Rect) => {
      if (cancelled) return;
      if (rectsEqual(lastRectRef.current, next)) return; // skip no-op re-renders
      lastRectRef.current = next;
      setRect(next);
    };

    const locate = (): HTMLElement | null =>
      document.querySelector(step.selector) as HTMLElement | null;

    // For targets inside a hover/focus-revealed panel (the Sync column), force
    // that panel open so the highlighted control is actually visible. We stash
    // the panel's original inline styles and restore them on cleanup.
    let revealedPanel: HTMLElement | null = null;
    let prevWidth = "";
    let prevOpacity = "";
    let prevPointer = "";
    const applyReveal = (el: HTMLElement) => {
      if (!step.revealParent || revealedPanel) return;
      const panel = el.parentElement as HTMLElement | null;
      if (!panel) return;
      revealedPanel = panel;
      prevWidth = panel.style.width;
      prevOpacity = panel.style.opacity;
      prevPointer = panel.style.pointerEvents;
      panel.style.width = "90px";
      panel.style.opacity = "1";
      panel.style.pointerEvents = "auto";
    };

    const tryEngage = () => {
      const el = locate();
      if (el) {
        applyReveal(el);
        if (scrolledForStep.current !== stepIndex) {
          scrolledForStep.current = stepIndex;
          const r = el.getBoundingClientRect();
          if (step.pinTop) {
            // Park the target just under the header so the tooltip fits below
            // it instead of covering the part the player has to interact with.
            const pin = step.pinTop;
            window.scrollTo({ top: window.scrollY + r.top - pin, behavior: "smooth" });
            // The page can still shift as the tour starts (the example card
            // appears), so check again once things settle.
            window.setTimeout(() => {
              const now = locate();
              if (cancelled || !now) return;
              const off = now.getBoundingClientRect().top - pin;
              if (Math.abs(off) > 8) window.scrollTo({ top: window.scrollY + off, behavior: "smooth" });
            }, 600);
          } else {
            // Tall elements: align near the top; otherwise center it.
            const tall = r.height > window.innerHeight * 0.6;
            el.scrollIntoView({ behavior: "smooth", block: tall ? "start" : "center" });
          }
        }
        commit(measure(el));
        return true;
      }
      return false;
    };

    if (!tryEngage()) {
      findTimer = window.setInterval(() => {
        attempts += 1;
        if (tryEngage()) {
          window.clearInterval(findTimer);
        } else if (attempts >= 30) {
          // ~3s on the correct tab and still nothing — skip this step.
          window.clearInterval(findTimer);
          if (!cancelled) goNext();
        }
      }, 100);
    }

    // Keep the spotlight glued to the element through scrolls / layout shifts.
    let raf = 0;
    const reposition = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = locate();
        if (el && !cancelled) commit(measure(el));
      });
    };
    const tracker = window.setInterval(reposition, 250);
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);

    return () => {
      cancelled = true;
      window.clearInterval(findTimer);
      window.clearInterval(tracker);
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
      // Restore the revealed panel to its original (hover-driven) behaviour.
      if (revealedPanel) {
        revealedPanel.style.width = prevWidth;
        revealedPanel.style.opacity = prevOpacity;
        revealedPanel.style.pointerEvents = prevPointer;
      }
    };
  }, [engaged, stepIndex, step.selector, goNext]);

  // Gated steps: watch the target's data-tour-painted flag (and pick up the
  // timezone label) so Next unlocks the moment the player paints an hour.
  const [gateMet, setGateMet] = useState(true);
  const [tzLabel, setTzLabel] = useState<string | null>(null);
  useEffect(() => {
    if (!engaged) return;
    const read = () => {
      const el = document.querySelector(step.selector) as HTMLElement | null;
      setTzLabel(el?.getAttribute("data-tour-tz") || null);
      setGateMet(step.gate !== "painted" || el?.getAttribute("data-tour-painted") === "true");
    };
    read();
    const t = window.setInterval(read, 250);
    return () => window.clearInterval(t);
  }, [engaged, step.selector, step.gate]);

  const quickFill = (preset: string) => {
    window.dispatchEvent(new CustomEvent("lfm:tour-quickfill", { detail: { preset } }));
  };

  // Measure tooltip height so placement math is accurate.
  useLayoutEffect(() => {
    if (engaged && rect && tipRef.current) {
      const h = tipRef.current.offsetHeight;
      if (h && Math.abs(h - tipH) > 4) setTipH(h);
    }
  }, [engaged, rect, stepIndex, tipH]);

  // Esc collapses to the pill (friendlier than nuking it outright).
  useEffect(() => {
    if (!engaged) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMinimized(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [engaged]);

  // Let other guides (the knight TutorialHelper) step aside while we run, so a
  // brand-new player never gets two tutorials competing for attention.
  useEffect(() => {
    const active = phase !== "idle";
    try {
      (window as any).__lfmTourActive = active;
    } catch {
      /* ignore */
    }
    window.dispatchEvent(new CustomEvent("lfm:tour-active", { detail: { active } }));
    return () => {
      try {
        (window as any).__lfmTourActive = false;
      } catch {
        /* ignore */
      }
    };
  }, [phase]);

  // Confetti burst geometry — generated once, reused for every celebration.
  const confetti = useMemo(() => {
    const colors = ["#f59e0b", "#fbbf24", "#fcd34d", "#ffffff", "#fb923c", "#34d399"];
    return Array.from({ length: 46 }, (_, i) => {
      const angle = (Math.PI * 2 * i) / 46 + (Math.random() - 0.5) * 0.4;
      const dist = 130 + Math.random() * 320;
      return {
        id: i,
        dx: Math.round(Math.cos(angle) * dist),
        dy: Math.round(Math.sin(angle) * dist),
        color: colors[i % colors.length],
        size: 6 + Math.round(Math.random() * 9),
        delay: Math.round(Math.random() * 140),
        rot: Math.round(Math.random() * 720 - 360),
        round: Math.random() > 0.5,
      };
    });
  }, []);

  // Auto-dismiss the celebration after it plays.
  useEffect(() => {
    if (phase !== "celebrating") return;
    const t = window.setTimeout(() => setPhase("idle"), 4600);
    return () => window.clearTimeout(t);
  }, [phase]);

  // --- Render: celebration -------------------------------------------------

  if (phase === "celebrating") {
    return (
      <div
        className="fixed inset-0 z-[9995] flex items-center justify-center overflow-hidden"
        style={{ pointerEvents: "auto", animation: "tourCelebFade 0.4s ease-out both" }}
        onClick={() => setPhase("idle")}
      >
        <style>{`
          @keyframes tourCelebFade { 0% { opacity: 0; } 100% { opacity: 1; } }
          @keyframes tourCelebOut { 0% { opacity: 1; } 100% { opacity: 0; } }
          @keyframes tourReadyPop {
            0% { opacity: 0; transform: scale(0.4); filter: blur(10px); }
            55% { opacity: 1; transform: scale(1.09); filter: blur(0); }
            72% { transform: scale(0.97); }
            100% { transform: scale(1); }
          }
          @keyframes tourSubIn { 0% { opacity: 0; transform: translateY(10px); } 100% { opacity: 1; transform: translateY(0); } }
          @keyframes tourBurst {
            0% { opacity: 0; transform: translate(-50%, -50%) rotate(0deg) scale(0.3); }
            12% { opacity: 1; }
            100% { opacity: 0; transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) rotate(var(--rot)) scale(1); }
          }
          @keyframes tourRingPulse {
            0% { opacity: 0.7; transform: translate(-50%, -50%) scale(0.2); }
            100% { opacity: 0; transform: translate(-50%, -50%) scale(2.4); }
          }
        `}</style>

        {/* Dim + amber vignette */}
        <div className="absolute inset-0 bg-[#04060c]/80" />
        <div
          className="absolute left-1/2 top-1/2 h-[60vmin] w-[60vmin] -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{ background: "radial-gradient(circle, rgba(245,158,11,0.28) 0%, rgba(245,158,11,0) 70%)" }}
        />

        {/* Expanding shockwave rings */}
        <div
          className="absolute left-1/2 top-1/2 h-40 w-40 rounded-full border-2 border-amber-400/60"
          style={{ animation: "tourRingPulse 1.2s ease-out forwards" }}
        />
        <div
          className="absolute left-1/2 top-1/2 h-40 w-40 rounded-full border border-amber-300/40"
          style={{ animation: "tourRingPulse 1.2s ease-out 0.18s forwards" }}
        />

        {/* Confetti */}
        {confetti.map((p) => (
          <span
            key={p.id}
            className="absolute left-1/2 top-1/2"
            style={
              {
                width: p.size,
                height: p.size,
                background: p.color,
                borderRadius: p.round ? "9999px" : "2px",
                boxShadow: `0 0 8px ${p.color}`,
                animation: `tourBurst 1.5s cubic-bezier(0.15,0.6,0.3,1) ${p.delay}ms forwards`,
                ["--dx" as any]: `${p.dx}px`,
                ["--dy" as any]: `${p.dy}px`,
                ["--rot" as any]: `${p.rot}deg`,
              } as React.CSSProperties
            }
          />
        ))}

        {/* Headline */}
        <div className="relative z-10 flex flex-col items-center px-6 text-center">
          <div
            className="mb-4 grid h-16 w-16 place-items-center rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 shadow-2xl shadow-amber-500/40"
            style={{ animation: "tourReadyPop 0.7s cubic-bezier(0.175,0.885,0.32,1.275) both" }}
          >
            {/* Crossed swords */}
            <svg className="h-9 w-9 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M14.5 14.5L21 21M21 21h-3.5M21 21v-3.5M3 3l9.5 9.5M3 3v3.5M3 3h3.5M21 3l-6.5 6.5M21 3h-3.5M21 3v3.5M3 21l6.5-6.5M3 21h3.5M3 21v-3.5" />
            </svg>
          </div>

          <h1
            className="bg-gradient-to-b from-amber-200 via-amber-400 to-amber-500 bg-clip-text text-[clamp(2.5rem,9vw,5.5rem)] font-black uppercase leading-none tracking-tight text-transparent drop-shadow-[0_2px_20px_rgba(245,158,11,0.5)]"
            style={{ animation: "tourReadyPop 0.7s cubic-bezier(0.175,0.885,0.32,1.275) 0.08s both" }}
          >
            Ready to Raid
          </h1>

          <p
            className="mt-3 max-w-md text-[15px] font-medium text-white/70"
            style={{ animation: "tourSubIn 0.5s ease-out 0.5s both" }}
          >
            Your character is all set up!{" "}
            {charName ? (
              <>
                <span className="font-bold text-amber-300">{charName}</span> enters the fray.
              </>
            ) : (
              "Time to enter the fray."
            )}
          </p>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setPhase("idle");
            }}
            className="mt-7 rounded-xl bg-amber-500 px-7 py-2.5 text-sm font-bold uppercase tracking-wide text-black shadow-lg shadow-amber-900/40 transition-colors hover:bg-amber-400"
            style={{ animation: "tourSubIn 0.5s ease-out 0.65s both" }}
          >
            Let's go
          </button>
        </div>
      </div>
    );
  }

  if (phase !== "active") return null;

  // --- Render: pill (waiting / collapsed) ----------------------------------

  const showSpotlight = engaged && rect;

  if (!showSpotlight) {
    return (
      <button
        type="button"
        onClick={() => {
          // Resume: head to the feature page; the tab watcher re-engages on arrival.
          if (currentTabRef.current !== step.tab) onRequestTab?.(step.tab);
          else {
            scrolledForStep.current = -1;
            setMinimized(false);
          }
        }}
        className="group fixed bottom-5 left-5 z-[9992] flex items-center gap-3 rounded-2xl border border-amber-400/40 bg-[#15110a]/95 px-4 py-3 shadow-2xl shadow-black/60 backdrop-blur-md transition-all hover:border-amber-400/70 hover:bg-[#1c150b]"
        style={{ animation: "tourPillIn 0.4s cubic-bezier(0.175,0.885,0.32,1.275) both" }}
      >
        <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-500/15 ring-1 ring-amber-400/40">
          <svg className="h-5 w-5 text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            {step.icon}
          </svg>
          <span className="absolute -right-1 -top-1 flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-400" />
          </span>
        </span>
        <span className="flex flex-col items-start leading-tight">
          <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-amber-300/80">
            Guided setup · Step {stepIndex + 1} of {STEPS.length}
          </span>
          <span className="text-[15px] font-bold text-white">Continue setup</span>
        </span>
        <svg className="h-4 w-4 text-amber-300/60 transition-transform group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
        </svg>
        <span
          role="button"
          tabIndex={0}
          aria-label="Dismiss setup guide"
          onClick={(e) => {
            e.stopPropagation();
            finish();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.stopPropagation();
              finish();
            }
          }}
          className="ml-1 grid h-6 w-6 place-items-center rounded-lg text-white/30 transition-colors hover:bg-white/10 hover:text-white/70"
        >
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </span>
        <style>{`@keyframes tourPillIn{0%{opacity:0;transform:translateY(16px) scale(0.92)}100%{opacity:1;transform:translateY(0) scale(1)}}`}</style>
      </button>
    );
  }

  // --- Render: spotlight + tooltip -----------------------------------------

  const r = rect!;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const spotTop = r.top - SPOT_PAD;
  const spotLeft = r.left - SPOT_PAD;
  const spotW = r.width + SPOT_PAD * 2;
  const spotH = r.height + SPOT_PAD * 2;

  // Decide tooltip side: respect preference, flip if there's no room.
  const spaceAbove = spotTop;
  const spaceBelow = vh - (spotTop + spotH);
  let placeBelow = step.placement === "bottom";
  if (placeBelow && spaceBelow < tipH + GAP + 12) placeBelow = false;
  if (!placeBelow && spaceAbove < tipH + GAP + 12) placeBelow = spaceBelow > spaceAbove;

  const tipTop = placeBelow
    ? clamp(spotTop + spotH + GAP, 12, vh - tipH - 12)
    : clamp(spotTop - GAP - tipH, 12, vh - tipH - 12);

  // Horizontally center the tooltip on the target, clamped to the viewport.
  const tipW = Math.min(step.wide ? TIP_W_WIDE : TIP_W, vw - 24);
  const targetCenterX = r.left + r.width / 2;
  const tipLeft = clamp(targetCenterX - tipW / 2, 12, vw - tipW - 12);

  // Arrow x position (relative to tooltip), pointing back at the target.
  const arrowLeft = clamp(targetCenterX - tipLeft, 22, tipW - 22);

  return (
    <div className="fixed inset-0 z-[9990]" style={{ pointerEvents: "none" }} aria-live="polite">
      <style>{`
        @keyframes tourSpotPulse {
          0%,100% { box-shadow: 0 0 0 9999px rgba(2,4,9,0.74), 0 0 0 2px rgba(245,158,11,1), 0 0 0 7px rgba(245,158,11,0.22), 0 0 30px 8px rgba(245,158,11,0.38); }
          50%     { box-shadow: 0 0 0 9999px rgba(2,4,9,0.74), 0 0 0 3px rgba(245,158,11,1), 0 0 0 12px rgba(245,158,11,0.12), 0 0 42px 13px rgba(245,158,11,0.52); }
        }
        @keyframes tourTipIn { 0% { opacity:0; transform: translateY(10px) scale(0.96);} 100% { opacity:1; transform: translateY(0) scale(1);} }
      `}</style>

      {/* Spotlight — pointer-events:none so the whole page stays clickable. */}
      <div
        style={{
          position: "fixed",
          top: spotTop,
          left: spotLeft,
          width: spotW,
          height: spotH,
          borderRadius: step.radius,
          pointerEvents: "none",
          transition: moveAnim
            ? "top 0.35s cubic-bezier(0.4,0,0.2,1), left 0.35s cubic-bezier(0.4,0,0.2,1), width 0.35s cubic-bezier(0.4,0,0.2,1), height 0.35s cubic-bezier(0.4,0,0.2,1)"
            : "none",
          animation: "tourSpotPulse 2.4s ease-in-out infinite",
        }}
      />

      {/* Tooltip — the only part that captures pointer events. */}
      <div
        ref={tipRef}
        role="dialog"
        aria-label={step.title}
        style={{
          position: "fixed",
          top: tipTop,
          left: tipLeft,
          width: tipW,
          pointerEvents: "auto",
          animation: "tourTipIn 0.28s cubic-bezier(0.175,0.885,0.32,1.275) both",
        }}
        className="rounded-2xl border border-amber-400/40 bg-gradient-to-b from-[#1a1610] to-[#0f0c08] shadow-2xl shadow-black/70"
      >
        {/* Arrow */}
        <div
          style={{
            position: "absolute",
            left: arrowLeft - 8,
            ...(placeBelow ? { top: -8 } : { bottom: -8 }),
            width: 16,
            height: 16,
            transform: "rotate(45deg)",
            background: placeBelow ? "#1a1610" : "#0f0c08",
            borderTop: placeBelow ? "1px solid rgba(245,158,11,0.45)" : "none",
            borderLeft: placeBelow ? "1px solid rgba(245,158,11,0.45)" : "none",
            borderBottom: placeBelow ? "none" : "1px solid rgba(245,158,11,0.45)",
            borderRight: placeBelow ? "none" : "1px solid rgba(245,158,11,0.45)",
          }}
        />

        {/* Top accent line */}
        <div className="absolute inset-x-0 top-0 h-px rounded-t-2xl bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" />

        <div className="p-5">
          {/* Header */}
          <div className="mb-3 flex items-start gap-3.5">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-amber-400/25 to-amber-600/10 ring-1 ring-amber-400/50 shadow-inner">
              <svg className="h-[22px] w-[22px] text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                {step.icon}
              </svg>
            </span>
            <div className="min-w-0 flex-1 pt-0.5">
              <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-amber-300/90">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-amber-400" />
                </span>
                Guided setup · Step {stepIndex + 1} of {STEPS.length}
              </div>
              <h3 className="mt-1 text-[18px] font-black leading-tight text-white">{step.title}</h3>
            </div>
            <button
              type="button"
              onClick={() => setMinimized(true)}
              aria-label="Minimize guide"
              className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-white/35 transition-colors hover:bg-white/10 hover:text-white/80"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {/* Body. The paint step adapts if hours are already set, and names
              the timezone the grid is drawn in. */}
          <p className="text-[14.5px] leading-relaxed text-white/75">
            {step.key === "availability" && (availabilitySet || (step.gate === "painted" && gateMet))
              ? "Nice, you've got hours painted. Drag over cells to add more, or drag over painted ones to clear them. Auto-scheduling and raid leaders only put you in raids inside these hours."
              : step.body}
            {step.key === "availability" && tzLabel && (
              <span className="mt-2 block text-[13px] text-white/55">
                Times are in your timezone: <span className="font-semibold text-cyan-300">{tzLabel}</span>.
              </span>
            )}
          </p>

          {/* Quick fill: a one-click start the player can then adjust. */}
          {step.quickFill && (
            <div className="mt-4">
              <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.16em] text-white/45">
                Quick fill <span className="normal-case tracking-normal font-medium text-white/35">· adds to what you've painted</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {TOUR_QUICK_FILLS.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => quickFill(p.key)}
                    title={p.hint}
                    className="rounded-lg border border-cyan-400/35 bg-cyan-400/10 px-3 py-1.5 text-[12.5px] font-semibold text-cyan-200 transition-colors hover:border-cyan-300/70 hover:bg-cyan-400/20"
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Gate status */}
          {step.gate === "painted" && (
            <div className={`mt-4 flex items-center gap-2 text-[12.5px] font-semibold ${gateMet ? "text-emerald-300" : "text-white/50"}`}>
              <span className={`grid h-4 w-4 place-items-center rounded-full border ${gateMet ? "border-emerald-400 bg-emerald-400/20" : "border-white/30"}`}>
                {gateMet && (
                  <svg className="h-2.5 w-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={4}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </span>
              {gateMet ? "Hours painted. Saved automatically." : "Paint at least one hour to continue"}
            </div>
          )}

          {/* Footer */}
          <div className="mt-5 flex items-center justify-between">
            {/* Progress dots */}
            <div className="flex items-center gap-1.5">
              {STEPS.map((s, i) => (
                <span
                  key={s.key}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === stepIndex ? "w-6 bg-amber-400" : i < stepIndex ? "w-2 bg-amber-400/60" : "w-2 bg-white/15"
                  }`}
                />
              ))}
            </div>

            <div className="flex items-center gap-2">
              {stepIndex > 0 && (
                <button
                  type="button"
                  onClick={goBack}
                  className="rounded-lg px-3 py-2 text-[13px] font-semibold text-white/55 transition-colors hover:bg-white/10 hover:text-white/90"
                >
                  Back
                </button>
              )}
              {!gateMet && (
                <button
                  type="button"
                  onClick={goNext}
                  className="rounded-lg px-3 py-2 text-[13px] font-semibold text-white/45 transition-colors hover:bg-white/10 hover:text-white/80"
                >
                  Skip
                </button>
              )}
              <button
                type="button"
                onClick={goNext}
                disabled={!gateMet}
                className="flex items-center gap-1.5 rounded-xl bg-amber-500 px-5 py-2 text-[13px] font-bold uppercase tracking-wide text-black shadow-lg shadow-amber-900/30 transition-colors hover:bg-amber-400 disabled:cursor-not-allowed disabled:bg-amber-500/30 disabled:text-black/50 disabled:shadow-none"
              >
                {isLast ? "Finish" : "Next"}
                {!isLast && (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Skip-all */}
        <button
          type="button"
          onClick={finish}
          className="w-full rounded-b-2xl border-t border-white/[0.06] py-2.5 text-[12px] font-medium text-white/35 transition-colors hover:bg-white/[0.03] hover:text-white/60"
        >
          Skip the tour
        </button>
      </div>
    </div>
  );
}

// Expose debug controls (mirrors the existing TutorialHelper convention).
// Run `onboardingTour.help()` in the browser console for the full list.
if (typeof window !== "undefined") {
  const warnIfUnmounted = () => {
    if (!(window as any).__lfmTourMounted) {
      console.warn(
        "[OnboardingTour] Component isn't mounted. Make sure you're logged in; it only renders for signed-in users."
      );
      return false;
    }
    return true;
  };

  (window as any).onboardingTour = {
    /** Force the full tour from step 1, ignoring the one-time flag. */
    start: () => {
      warnIfUnmounted();
      startOnboardingTour();
    },
    /** Jump straight to a step: 0=paint, 1=confirm, 2=weeks, 3=guild, 4=sync, 5=BiS, 6=squad. */
    goto: (index: number = 0) => {
      warnIfUnmounted();
      window.dispatchEvent(new CustomEvent(TOUR_EVENT, { detail: { action: "goto", index } }));
    },
    /** Simulate the REAL trigger (respects the one-time flag + pending/pill flow). */
    request: () => {
      warnIfUnmounted();
      requestOnboardingTour();
    },
    /** Preview the "Ready to Raid" completion celebration. */
    celebrate: () => {
      warnIfUnmounted();
      window.dispatchEvent(new CustomEvent(TOUR_EVENT, { detail: { action: "celebrate" } }));
    },
    /** Clear the one-time flag so the real character-creation trigger fires again. */
    reset: () => {
      resetOnboardingTour();
      console.info("[OnboardingTour] Flag cleared. Create a character, or run onboardingTour.start(), to see it again.");
    },
    /** Current persisted state: 'pending' | 'done' | null. */
    status: () => readFlag(),
    /** List the steps. */
    steps: () => STEPS.map((s, i) => `${i}: ${s.key} → ${s.title}`),
    /** Print this help. */
    help: () => {
      console.info(
        [
          "%c OnboardingTour test commands ",
          "",
          "  onboardingTour.start()       Force-play the full tour from step 1",
          "  onboardingTour.goto(n)       Jump to a step (0=paint, 1=confirm, 2=weeks, 3=guild, 4=sync, 5=BiS, 6=squad)",
          "  onboardingTour.request()     Simulate a real character-creation trigger (one-time + pill flow)",
          "  onboardingTour.celebrate()   Preview the 'Ready to Raid' completion celebration",
          "  onboardingTour.reset()       Clear the 'seen' flag so creating a character shows it again",
          "  onboardingTour.status()      'pending' | 'done' | null",
          "  onboardingTour.steps()       List the steps",
          "",
          "Tip: to test the adaptive pill, run reset() then request() while NOT on the Roster tab.",
          "You'll see the 'Continue setup' pill, and it engages when you open the Roster.",
        ].join("\n"),
        "background:#f59e0b;color:#000;font-weight:bold;border-radius:3px"
      );
    },
  };

  console.info(
    "%c[OnboardingTour]%c ready · run %conboardingTour.help()%c to test",
    "color:#f59e0b;font-weight:bold",
    "color:inherit",
    "color:#f59e0b",
    "color:inherit"
  );
}
